import { and, eq } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@boveda/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ────────────────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can
 * match **ZERO rows and still return "success"**. This is not a hypothesis: it was measured in
 * `space-academy-app` (same canon, `activity` table in the local stack) — a
 * row is created, shortly after another is created, and **in between there is no
 * `update` op**: the value the user had just written never made it to
 * generate a CRUD operation.
 *
 * The mechanism: when the CRUD queue empties, PowerSync **rebuilds the
 * local view from the last checkpoint**. If the checkpoint does not yet contain
 * the newly created row, the row is **ABSENT for that instant** — and an
 * UPDATE against it writes nothing, generates no op, and **does not fail**.
 *
 * In a vault/secret manager the damage is of a different kind than in a
 * todo-app: if the user ROTATES a credential or edits a newly
 * created entry and the write is silently lost, they still believe the
 * new credential is saved while the local view (and therefore what
 * gets synchronized) still has the OLD one. `packages/shared/src/vault/mutations.ts`
 * and `packages/shared/src/users/mutations.ts` are the two surfaces in this
 * repo that perform `UPDATE … WHERE id = ?` against the view.
 *
 * Why the absence of an error proves absolutely nothing here: the server
 * write path returns **2xx always by design** (a 4xx would
 * block the client queue forever). A user write that
 * disappears without a trace is the worst possible failure mode in this layer.
 *
 * Contract of this module: write, **READ BACK**, compare. If the value
 * does not land, retry with backoff; if it still does not land after the retries are
 * exhausted, **THROW** — the caller reports it instead of leaving the user looking at
 * a field that does not represent anything saved.
 *
 * ── What this module does NOT cover (deliberately) ────────────────────────
 * Hard `DELETE`s (`db.delete(...)`) are not verified: their natural
 * check would be "the row is no longer there", and "absent" is exactly the ambiguous state
 * of this window — you cannot distinguish "it was deleted" from "it had not yet
 * arrived". This repo has no hard `DELETE` against the vault today (deleting
 * items/attachments is ALWAYS a soft delete via `deleted_at`, which IS
 * verified like any other UPDATE). A signal that would reopen this: a real hard DELETE
 * being lost (a row reappearing after purging).
 *
 * It also does not cover `upsert`s that decide INSERT-vs-UPDATE with a `SELECT`
 * within the same window: that `SELECT` may see the row missing and
 * retry the INSERT on an id that already exists. The correct approach there is not
 * stronger verification, but splitting the upsert into explicit `create`/`update`
 * calls so the caller (which already knows whether the entity is new) does not have to
 * guess — as resolved in `vault/mutations.ts`
 * (`createVaultItemMutation`/`updateVaultItemMutation`).
 *
 * ── `scope`: vault/organization isolation, not just correctness ────────────
 * Brought from `comandero-app` (`f8f1049`), where removing the
 * `organization_id` condition from the `WHERE` while migrating to `patchRowVerified` opened
 * cross-organization writes: `infra/sync_streams.yaml`
 * synchronizes ALL organizations the user is a `member` of (not
 * only the active one — the `org_vaults` stream brings `vault_item`/`vault_attachment`
 * from `my_org_vault_ids`, the union of ALL the user's organization vaults
 * ), and the row's `id` can arrive via URL or through the
 * synchronization itself. Without an extra filter, an `UPDATE … WHERE id = ?` launched from
 * the context of vault A could land on a row in vault B if the
 * `id` matched — the tenant `WHERE` was not redundant with the PK; it was
 * isolation in depth. In this repo `vault_item`/`vault_attachment` do not
 * have their own `organization_id` column (the org hangs from `vault`, not from
 * its child rows); the real row-level tenant boundary is
 * `vault_id` — constraining by it provides organization isolation (a
 * vault belongs to only one org) and something stricter (it also isolates
 * different personal/organization vaults).
 *
 * `vault_id` is not a matter of convenience: it is the SAME authorization boundary
 * the server already uses. `packages/core-rust/src/db/sync.rs`
 * (`authorized_vault`) had — and closed — the same server-side hole:
 * it previously checked permission against the `vault_id` the client
 * CLAIMED while the SQL located the row only by `id`, so declaring one's own vault
 * was enough to overwrite another person's encrypted envelope. It was
 * fixed by resolving the EFFECTIVE `vault_id` (the one on the row in the DB, not the
 * reclamado) y comprobando `vault_access`/`vault_admin`
 * (`packages/core-rust/src/db/access.rs`) against THAT vault — personal owner or
 * member of the vault's organization, the only definition of authorization
 * in the domain. And the encryption layer (`packages/shared/src/crypto/vault.ts`)
 * binds the envelope to its `vault_id` in the AAD: moving it to another vault makes
 * decryption fail. Three independent layers agree that `vault_id` is the
 * real boundary — it is not an invented `organization_id` just to have SOMETHING to
 * include in `scope`.
 *
 * `scope` restores that filter **applied to both the UPDATE and the
 * read-back**: if it applied to only one of the two, verification
 * would lie. A row that does not satisfy the scope is
 * treated like a missing row: the write is never confirmed and the
 * function ends by throwing `LocalWriteLostError`.
 *
 * ── `ThemeProvider` guard (2026-08-13): closes the root cause, not the
 *    need for this module ────────────────────────────────────────────────
 * `ThemeProvider` (`apps/web/src/providers/theme-provider.tsx`) had a
 * `createEffect` that seeded the `user` row locally (SELECT+INSERT) for
 * ANY `uid`, not just the guest — it competed with the row that the server
 * already creates unconditionally on all signups/OAuth
 * (`packages/core-rust/src/db/user.rs:101,350`) during the SAME checkpoint
 * window described above: the measured root cause of the `user` mutations
 * this module protects. It was closed with a guard (`uid !== VISITOR_ID`,
 * the same pattern as `i18n-provider.tsx`/`typography-provider.tsx`, canon
 * `listto-app` `e72a1dd2`); measurement in
 * `packages/shared/src/users/__tests__/theme-provider-seed-window.test.ts`.
 *
 * This does NOT make `patchRowVerified` optional for `user`, for three
 * independent reasons: (1) the guest STILL seeds locally — it has no
 * server to bring it the row, so the window still exists for
 * it; (2) the checkpoint window is GENERAL — any UPDATE just
 * after a `user` row lands via sync (signup, or a language/theme change made
 * immediately after account creation) suffers it too, with or without the
 * local seed that originally triggered it; and (3) the isolation
 * `scope`/`vault_id` from the section above is an INDEPENDENT mechanism —
 * it protects against writing to ANOTHER vault/tenant's row, not against
 * writing too early — so closing the root cause of the seed neither touches
 * nor reduces that need for `vault_item`/`vault_attachment`.
 */

/**
 * Retry backoff. Total ≈ 6,05 s: more than enough for the round trip of a
 * checkpoint (measured at ~1 s against the local stack) without leaving a fast
 * quick interaction —renaming an entry, rotating a credential— hanging
 * forever if the row truly does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/** Additional equality condition — defense-in-depth tenant isolation.*/
export interface ScopeCondition {
  column: SQLiteColumn;
  value: unknown;
}

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests do not take seconds.*/
  backoffMs?: readonly number[];
  /**
   * Extra conditions the row must ALSO satisfy, in addition to `id` —
   * typically `vault_id`/`organization_id`. They apply to both the UPDATE and
   * the read-back: a row that does not satisfy the scope is treated like a
   * absent row (see security note above).
   */
  scope?: readonly ScopeCondition[];
}

/** Error for a local-first write that did not land in the view.*/
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Builds `eq(id) AND eq(scope1) AND …` — or just `eq(id)` with no scope.*/
function idAndScopeCondition(idColumn: SQLiteColumn, id: string, scope: readonly ScopeCondition[]) {
  if (scope.length === 0) {
    return eq(idColumn, id);
  }
  return and(eq(idColumn, id), ...scope.map((s) => eq(s.column, s.value)));
}

/**
 * Reads the row by id (+ `scope`, if provided), or `undefined` if it is not in the
 * local view or does not satisfy the scope — the same result as "missing row", by
 * design (see the security note above).
 */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  scope: readonly ScopeCondition[] = [],
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read
  // is by primary key, so the row is a flat record of columns.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  const rows = (await db
    .select()
    .from(table)
    .where(idAndScopeCondition(idColumn, id, scope))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** `null` and `undefined` are the same "empty" value when compared to SQLite.*/
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * **Verified** `UPDATE … WHERE id = ?`: writes, rereads, compares the patch
 * of the patch and retries while they have not landed. Throws `LocalWriteLostError`
 * if they still are not after the backoff is exhausted.
 *
 * `updated_at` is set by this function on every attempt (and is not compared: it changes
 * on every pass); the `patch` must contain only domain fields.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(idAndScopeCondition(idColumn, id, scope));

    const row = await readRow(db, table, idColumn, id, scope);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    await sleep(wait);
  }
}

/**
 * Waits for the row to EXIST in the local view before touching it (+ `scope`,
 * if provided, with the same isolation criterion as `patchRowVerified`).
 *
 * For mutations that are not a simple patch. Retrying inside a transaction is not
 * appropriate there (it would sleep with the write lock held), and the
 * damage from operating on a missing row may be worse than losing a
 * field. This repo has no transactional mutation of this type on the vault today,
 * but the primitive is ready for when one exists (e.g. moving a vault item in bulk).
 * move an item between vaults in bulk).
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const scope = options.scope ?? [];

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id, scope)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}
